import { EthConnection } from '@darkforest_eth/network';
import { GasPrices } from '@darkforest_eth/types';
import { getLocalGasPrices } from './LocalGasPriceAPI';

// Patch the xDaiApi module at runtime to prevent CORS calls
function patchXDaiApiModule() {
  try {
    // Try to access the xDaiApi module through require cache
    const networkModule = require('@darkforest_eth/network');

    // If the module has getAutoGasPrices, override it
    if (networkModule && typeof networkModule.getAutoGasPrices === 'function') {
      const originalGetAutoGasPrices = networkModule.getAutoGasPrices;

      networkModule.getAutoGasPrices = async function() {
        console.log('Intercepted getAutoGasPrices call, using local API');
        const prices = await getLocalGasPrices();
        if (prices) {
          return prices;
        }
        // Return default prices if local API fails
        return { slow: 1, average: 2, fast: 5 };
      };

      console.log('Successfully patched xDaiApi.getAutoGasPrices');
    }
  } catch (error) {
    console.warn('Could not patch xDaiApi module:', error);
  }
}

/**
 * Patch EthConnection to use our local gas price API instead of direct external calls
 * This prevents CORS errors when running in development
 */
export function patchEthConnectionGasPrices(ethConnection: EthConnection) {
  // First, try to patch the xDaiApi module
  patchXDaiApiModule();
  // Stop the existing polling interval to prevent CORS calls
  const gasPricesInterval = (ethConnection as any).gasPricesInterval;
  if (gasPricesInterval) {
    clearInterval(gasPricesInterval);
    console.log('Stopped original gas price polling interval');
  }

  // Override the refreshGasPrices method to use our local API
  (ethConnection as any).refreshGasPrices = async function() {
    try {
      const prices = await getLocalGasPrices();
      if (prices) {
        // Update internal gas prices
        (this as any).gasPrices = prices;
        // Publish to subscribers
        this.gasPrices$.publish(prices);
        // Update diagnostics if available
        if ((this as any).diagnosticsUpdater) {
          (this as any).diagnosticsUpdater.updateDiagnostics((d: any) => (d.gasPrices = prices));
        }
        console.log('Gas prices updated from local API:', prices);
      } else {
        console.warn('Local gas price API returned null, keeping current prices');
        // Don't fall back to original method to avoid CORS
      }
    } catch (error) {
      console.warn('Error in patched refreshGasPrices:', error);
      // Don't fall back to original method to avoid CORS
    }
  };

  // Override getAutoGasPrices to return our cached prices
  const originalGetAutoGasPrices = ethConnection.getAutoGasPrices.bind(ethConnection);

  ethConnection.getAutoGasPrices = function(): GasPrices {
    const currentPrices = (this as any).gasPrices;
    if (currentPrices) {
      return { ...currentPrices };
    }
    // Fall back to original method if no prices available
    return originalGetAutoGasPrices();
  };

  // Start our own polling with the patched method
  const refreshPatched = (ethConnection as any).refreshGasPrices.bind(ethConnection);
  refreshPatched(); // Call immediately
  const newInterval = setInterval(refreshPatched, 60000); // Every 60 seconds
  (ethConnection as any).gasPricesInterval = newInterval;

  console.log('EthConnection gas price methods patched to use local API');
}
